import { Suspense, useEffect } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router';
import { Splash } from '@/components/Splash';
import { UpgradeRequired } from '@/components/UpgradeRequired';
import { MainLayout } from '@/components/Layout';
import { useSession } from '@/stores/session';
import { useBoot } from '@/stores/boot';
import { useSync, setSync } from '@/sync/status';
import { onApiEvent } from '@/lib/api';
import { engine } from '@/sync/engine';
import { loadClock } from '@/lib/clock';
import { useNotifs } from '@/features/notifications/useUnread';
import { onDbChange } from '@/db';
import { applyCategoryTheme } from '@dd/ui';
import { useShop } from '@/hooks/useShop';
import { P, publicRoutes, mainTabs, subRoutes } from './routes';

/** Gate: who may see what (Bible 18.1 / 14.4). */
function Gate() {
  const { phase, profile } = useSession();
  const loc = useLocation();
  if (phase === 'boot') return <Splash />;
  if (phase === 'out') return <Navigate to={useSession.getState().known ? '/pin' : '/welcome'} replace state={{ from: loc.pathname }} />;
  if (phase === 'locked') return <Navigate to="/pin" replace />;
  if (!profile) return <Splash />;
  if (profile.mustChangePin && loc.pathname !== '/force-pin') return <Navigate to="/force-pin" replace />;
  if (!profile.shopId && profile.role === 'owner' && loc.pathname !== '/setup' && loc.pathname !== '/force-pin') return <Navigate to="/setup" replace />;
  return <Outlet />;
}

/** Public screens bounce signed-in users to the app. */
function PublicOnly() {
  const { phase, profile } = useSession();
  if (phase === 'boot') return <Splash />;
  if (phase === 'ready' && profile?.shopId) return <Navigate to="/" replace />;
  return <Outlet />;
}

/** Follows the open shop: icons, badges and highlight cards switch to its category colour (Bible 7.0). */
function CategoryTheme() {
  const { row, category } = useShop();
  useEffect(() => { applyCategoryTheme(row ? category : null); }, [row, category]);
  return null;
}

function Boot() {
  const boot = useSession((s) => s.boot);
  const phase = useSession((s) => s.phase);
  const shopId = useSession((s) => s.profile?.shopId);
  useEffect(() => { void loadClock(); void boot(); return onDbChange(() => { void loadClock(); }); }, [boot]);
  useEffect(() => {
    const off = onApiEvent((e) => {
      if (e.upgradeRequired) setSync({ needsUpgrade: true });
      if (e.unauthenticated && useSession.getState().phase === 'ready') {
        // refresh failed for a real reason (not just the network) → ask for the PIN again; local data stays on the phone
        void useSession.getState().logout();
      }
    });
    const on = () => { setSync({ online: true }); engine.kick(); };
    const offl = () => setSync({ online: false });
    window.addEventListener('online', on); window.addEventListener('offline', offl);
    return () => { off(); window.removeEventListener('online', on); window.removeEventListener('offline', offl); };
  }, []);
  useEffect(() => {
    if (phase !== 'ready' || !shopId) return;
    void useBoot.getState().restore().then(() => useBoot.getState().load());
    void useNotifs.getState().refresh?.();
  }, [phase, shopId]);
  useEffect(() => onDbChange(() => undefined), []);
  return null;
}

export function App() {
  const needsUpgrade = useSync((s) => s.needsUpgrade);
  if (needsUpgrade) return <UpgradeRequired />;
  return (
    <BrowserRouter>
      <Boot />
      <CategoryTheme />
      <Suspense fallback={<Splash />}>
        <Routes>
          <Route element={<PublicOnly />}>{publicRoutes.map((r) => <Route key={r.path} path={r.path} element={r.element} />)}</Route>
          <Route path="/pin" element={<P.PinLock />} />
          <Route path="/legal/:doc" element={<P.Legal />} />
          <Route element={<Gate />}>
            <Route path="/setup" element={<P.SetupFlow />} />
            <Route path="/force-pin" element={<P.ForcePin />} />
            <Route element={<MainLayout />}>{mainTabs.map((r) => <Route key={r.path} path={r.path} index={r.path === '/'} element={r.element} />)}</Route>
            {subRoutes.map((r) => <Route key={r.path} path={r.path} element={r.element} />)}
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
